import React, { useCallback, useEffect, useState } from "react";
import { MdAdd, MdEdit } from "react-icons/md";
import { toast } from "react-toastify";
import Modal from "../../../../components/modal";
import { ProjetoDoCliente } from "../../../../interfaces/ClienteEquipe";
import { FilaMembro, FilaOrganizacao } from "../../../../interfaces/FilaOrganizacao";
import { getProjetosDoCliente } from "../../../../services/clienteEquipeApi";
import {
  atualizarFila,
  criarFila,
  definirMembrosDaFila,
  getAtendentesDaOrganizacao,
  getFilasDaOrganizacao,
  getMembrosDaFila,
  mensagemDaFila,
} from "../../../../services/filaOrganizacaoApi";

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";
const lblCls = "text-sm font-semibold text-gray-700 dark:text-gray-300";

/** Mesma regra do backend: minúsculo, `[a-z0-9-]`. */
const normalizarAlias = (v: string) => v.toLowerCase().replace(/[^a-z0-9-]/g, "");

interface EditorProps {
  fila: FilaOrganizacao | null;
  /**
   * Quem pode entrar na fila: `GET fila/atendentes` (vínculo ativo + perfil com `itsm.fila.atender`).
   */
  candidatos: FilaMembro[];
  /** Projetos do cliente para o seletor (vazio se a pessoa não pode listá-los). */
  projetos: ProjetoDoCliente[];
  /** Rota `api/clientes/{orgId}/filas...`; undefined = rota da sessão (`api/ClientPortal/fila/filas...`). */
  orgId?: number;
  onClose: () => void;
  onSaved: () => void;
}

const FilaEditor: React.FC<EditorProps> = ({ fila, candidatos, projetos, orgId, onClose, onSaved }) => {
  const [name, setName] = useState(fila?.name ?? "");
  const [description, setDescription] = useState(fila?.description ?? "");
  const [alias, setAlias] = useState(fila?.enderecoAlias ?? "");
  const [ativa, setAtiva] = useState(fila?.ativa ?? true);
  const [projetoId, setProjetoId] = useState<string>(fila?.clientProjectId != null ? String(fila.clientProjectId) : "");
  const [membroIds, setMembroIds] = useState<number[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!fila) return;
    getMembrosDaFila(fila.id, orgId)
      .then((ms) => setMembroIds(ms.map((m) => m.pessoaId)))
      .catch(() => setErro("Não foi possível carregar os membros desta fila."));
  }, [fila, orgId]);

  // Projetos ativos + o atual da fila (mesmo que a lista não o traga), para o valor atual nunca se perder.
  const opcoesDeProjeto = projetos
    .filter((p) => p.status === "Active" || p.clientProjectId === fila?.clientProjectId)
    .map((p) => ({ id: p.clientProjectId, nome: p.nome }));
  if (fila?.clientProjectId != null && !opcoesDeProjeto.some((o) => o.id === fila.clientProjectId)) {
    opcoesDeProjeto.push({ id: fila.clientProjectId, nome: fila.projetoNome ?? `Projeto #${fila.clientProjectId}` });
  }

  const alternar = (id: number) =>
    setMembroIds((atual) => (atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id]));

  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      const corpo = {
        name: name.trim(),
        description: description.trim() || null,
        enderecoAlias: alias || null,
        ativa,
        // O PUT SUBSTITUI o projeto: sempre manda o valor atual (ou o escolhido), nunca omite.
        clientProjectId: projetoId === "" ? null : Number(projetoId),
      };
      const salva = fila ? await atualizarFila(fila.id, corpo, orgId) : await criarFila(corpo, orgId);
      const id = salva?.id ?? fila?.id;
      if (id && (fila || membroIds.length > 0)) await definirMembrosDaFila(id, membroIds, orgId);
      toast.success("Fila salva.");
      onSaved();
      onClose();
    } catch (e) {
      setErro(mensagemDaFila(e, "Não foi possível salvar a fila."));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div
        className="w-full max-w-lg rounded-[20px] border border-gray-100 bg-white p-6 shadow-dv-lg dark:border-white/10 dark:bg-navy-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold text-navy-700 dark:text-white">{fila ? "Editar fila" : "Nova fila"}</h3>
        <div className="mt-4 flex max-h-[70vh] flex-col gap-3 overflow-y-auto pr-1">
          <div>
            <label htmlFor="fila-nome" className={lblCls}>Nome *</label>
            <input id="fila-nome" className={inputCls} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label htmlFor="fila-descricao" className={lblCls}>Descrição</label>
            <textarea id="fila-descricao" rows={2} className={inputCls} value={description} maxLength={400} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div>
            <label htmlFor="fila-alias" className={lblCls}>Alias do e-mail</label>
            <input id="fila-alias" className={inputCls} value={alias} maxLength={64} onChange={(e) => setAlias(normalizarAlias(e.target.value))} />
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              {alias
                ? `E-mails enviados para suporte+${alias}@… entram nesta fila.`
                : "Sem alias, a fila não recebe chamados por e-mail. Use letras minúsculas, números e hífen."}
            </p>
          </div>
          <div>
            <label htmlFor="fila-projeto" className={lblCls}>Projeto</label>
            <select id="fila-projeto" className={inputCls} value={projetoId} onChange={(e) => setProjetoId(e.target.value)}>
              {/* A rota da sessão mantém o projeto quando vem null: não dá para desligar por ela. */}
              {!(orgId === undefined && fila?.clientProjectId != null) && <option value="">Sem projeto</option>}
              {opcoesDeProjeto.map((o) => (
                <option key={o.id} value={o.id}>{o.nome}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Fila de projeto só aceita membros que estão no projeto.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={ativa} onChange={(e) => setAtiva(e.target.checked)} />
            Fila ativa
          </label>

          <fieldset>
            <legend className={lblCls}>Membros</legend>
            {candidatos.length === 0 ? (
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Ainda não há atendentes cadastrados na organização para escolher.
              </p>
            ) : (
              <div className="mt-1 flex flex-col gap-1">
                {candidatos.map((c) => (
                  <label key={c.pessoaId} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input type="checkbox" checked={membroIds.includes(c.pessoaId)} onChange={() => alternar(c.pessoaId)} />
                    {c.nome} <span className="text-xs text-gray-600 dark:text-gray-400">({c.email})</span>
                  </label>
                ))}
              </div>
            )}
          </fieldset>

          {erro && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{erro}</p>}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={salvando || name.trim().length < 2}
            onClick={salvar}
            className="rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
          >
            {salvando ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

/** Aba "Filas": criar/editar filas e definir membros, para quem tem `itsm.fila.coordenar`. */
interface TabProps {
  /** Define a família de rotas: com valor, `api/clientes/{orgId}/filas...` (quem tem `cliente.equipe.gerir`). */
  orgId?: number;
  /** Cliente cujos projetos alimentam o seletor; na rota da sessão é a própria organização. */
  clienteId?: number;
}

const FilasTab: React.FC<TabProps> = ({ orgId, clienteId = orgId }) => {
  const [filas, setFilas] = useState<FilaOrganizacao[]>([]);
  const [candidatos, setCandidatos] = useState<FilaMembro[]>([]);
  const [projetos, setProjetos] = useState<ProjetoDoCliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [editando, setEditando] = useState<FilaOrganizacao | null | undefined>(undefined);

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    Promise.all([
      getFilasDaOrganizacao(orgId),
      getAtendentesDaOrganizacao(orgId),
      // Quem coordena sem poder listar projetos ainda edita a fila; só fica sem opções novas.
      clienteId === undefined ? Promise.resolve([]) : getProjetosDoCliente(clienteId).catch(() => []),
    ])
      .then(([lista, atendentes, projs]) => {
        setFilas(lista);
        setProjetos(projs);
        setCandidatos([...atendentes].sort((x, y) => x.nome.localeCompare(y.nome, "pt-BR")));
      })
      .catch((e) => setErro(mensagemDaFila(e, "Não foi possível carregar as filas e os atendentes. Tente de novo em instantes.")))
      .finally(() => setLoading(false));
  }, [orgId, clienteId]);

  useEffect(carregar, [carregar]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setEditando(null)}
          className="flex items-center gap-1.5 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 active:scale-[.98] dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
        >
          <MdAdd className="h-4 w-4" aria-hidden="true" />
          Nova fila
        </button>
      </div>

      {loading ? (
        <div role="status" className="py-12 text-center text-sm text-gray-700 dark:text-gray-300">Carregando…</div>
      ) : erro ? (
        <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">{erro}</p>
      ) : filas.length === 0 ? (
        <p className="rounded-xl border border-dashed border-gray-200 py-10 text-center text-sm text-gray-700 dark:border-white/10 dark:text-gray-300">
          Sua organização ainda não tem filas. Crie a primeira para começar a receber chamados.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {filas.map((f) => (
            <div key={f.id} className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 dark:border-white/5 dark:bg-navy-900">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-navy-700 dark:text-white">
                  {f.name}
                  {!f.ativa && <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-700 dark:bg-navy-700 dark:text-gray-300">Inativa</span>}
                </p>
                <p className="truncate text-xs text-gray-600 dark:text-gray-400">
                  {f.projetoNome ? `Projeto ${f.projetoNome} · ` : ""}
                  {f.membrosCount} {f.membrosCount === 1 ? "membro" : "membros"}
                  {f.enderecoAlias ? ` · suporte+${f.enderecoAlias}@…` : ""}
                </p>
              </div>
              <button
                type="button"
                aria-label={`Editar a fila ${f.name}`}
                onClick={() => setEditando(f)}
                className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-dv-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 dark:text-gray-400 dark:hover:bg-navy-700"
              >
                <MdEdit className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}

      {editando !== undefined && (
        <FilaEditor fila={editando} candidatos={candidatos} projetos={projetos} orgId={orgId} onClose={() => setEditando(undefined)} onSaved={carregar} />
      )}
    </div>
  );
};

export default FilasTab;
